import { Spinner } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { ItemRendererProps } from '@blueprintjs/select';
import classNames from 'classnames';
import * as React from 'react';
import { noop } from 'vet-bones/utils';

import { ItemQueryList } from 'src/components/select/ItemQueryList';
import { EmptyHome } from 'src/containers/Home/EmptyHome';
import { Header } from 'src/containers/Home/Header';
import { isProjectVisibleInContext } from 'src/containers/Home/isProjectVisibleInContext';
import { ProjectCard } from 'src/containers/Home/ProjectCard';
import { ProjectCards } from 'src/containers/Home/ProjectCards';
import { Project } from 'src/store/state/projects';
import { ReduxState } from 'src/store/types';
import { ProjectSortOption } from 'src/store/ui/types';
import * as copy from 'src/templates/copy';
import { getCurrentOrgDomain } from 'src/utils/getCurrentOrgDomain';
import { useCloudStorageBackupProjects } from 'src/utils/hooks/useCloudStorageBackupProjects';
import {
  CloudProject,
  useCloudStorageProjects,
} from 'src/utils/hooks/useCloudStorageProjects';
import { useDefaultPortalConnection } from 'src/utils/hooks/useDefaultPortalConnection';
import { usePortalConnectionsWithDemo } from 'src/utils/hooks/usePortalConnectionsWithDemo';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

const ProjectQueryList = ItemQueryList.ofType<Project | CloudProject>();
const projectListPredicate = (
  searchText: string,
  project: Project | CloudProject
) => project.name.toLowerCase().includes(searchText.toLowerCase());

const PROJECTS_DEBOUNCE_THRESHOLD = 100; // arbitrarily chosen; can adjust if we encounter issues

export const Home: React.VFC = () => {
  const defaultConnection = useDefaultPortalConnection();
  const portalConnections = usePortalConnectionsWithDemo();

  const projects = useReduxSelector((state: ReduxState) =>
    (state.projects.projectIds || [])
      .map((id) => state.projects.projectsById[id])
      .filter((project) => !project.isRemoved)
  );

  const backedUpCloudProjects = projects
    .filter((project) => project.cloudProjectId)
    .map((project) => project.cloudProjectId);

  const { data: cloudProjects, isLoading: isCloudProjectsLoading } =
    useCloudStorageProjects();

  const isLoadingConnections = React.useMemo(
    () => !portalConnections?.some((c) => c.id === defaultConnection?.id),
    [defaultConnection, portalConnections]
  );

  const currentOrgDomain = getCurrentOrgDomain();

  const compositeProjects = React.useMemo(
    () =>
      [
        ...projects.filter((project) => !project.isCloudProject),
        ...(cloudProjects || []).filter(
          (project) => !backedUpCloudProjects.includes(project.id)
        ),
      ].filter((project) =>
        isProjectVisibleInContext(project, {
          defaultConnection,
          portalConnections,
          currentOrgDomain,
        })
      ),
    [
      projects,
      cloudProjects,
      currentOrgDomain,
      defaultConnection,
      backedUpCloudProjects,
      portalConnections,
    ]
  );

  const projectSortOption = useReduxSelector(
    (state) => state.ui.projectSortOption
  );
  const sortedProjects = React.useMemo(() => {
    switch (projectSortOption) {
      case ProjectSortOption.NAME:
        return compositeProjects.sort((a, b) => a.name.localeCompare(b.name));
      case ProjectSortOption.UPDATED_AT:
        return compositeProjects.sort(
          (a, b) => b.dateLastUpdated - a.dateLastUpdated
        );
      case ProjectSortOption.CREATED_AT:
        return compositeProjects.sort((a, b) => b.dateCreated - a.dateCreated);

      default:
        return compositeProjects;
    }
  }, [projectSortOption, compositeProjects]);

  useCloudStorageBackupProjects();

  return (
    <div className="sd-home">
      <ProjectQueryList
        customRenderer={({ itemList }, queryInputElement) => {
          let body: React.ReactNode;
          if (
            !cloudProjects ||
            isCloudProjectsLoading ||
            isLoadingConnections
          ) {
            body = <Spinner />;
          } else if (sortedProjects?.length) {
            body = itemList;
          } else {
            body = <EmptyHome />;
          }
          return (
            <>
              <Header queryInputElement={queryInputElement} />
              <div
                className={classNames('sd-home__body', 'sd-home__body-full')}
              >
                {body}
              </div>
            </>
          );
        }}
        debounceOnInputChange={projects.length > PROJECTS_DEBOUNCE_THRESHOLD}
        inputProps={{
          leftIcon: IconNames.SEARCH,
          placeholder: copy.components.home.search,
        }}
        itemListRenderer={ProjectCards}
        itemPredicate={projectListPredicate}
        itemRenderer={(
          project: Project | CloudProject,
          { modifiers, index }: ItemRendererProps
        ) => {
          return (
            <ProjectCard
              key={project.id}
              hidden={!modifiers.matchesPredicate}
              idx={index}
              project={project}
            />
          );
        }}
        items={sortedProjects}
        onSelect={noop /* TODO */}
      />
    </div>
  );
};
